<template>
  <div id="postbox-container" :class="{ 'is-desktop': isDesktop }" class="wc-row">
    <div :class="computedClassesFilterOpen">
      <Card v-if="!showErrorTile">
        <template #content>
          <div v-if="!noDocuments">
            <div v-if="loading" class="card-space">
              <Spinner />
            </div>
            <div v-if="isFilterActivated" class="postbox-header-container">
              <div
                ref="filterButton"
                class="filter-row"
                tabindex="0"
                @click="toggleFilterWindow"
                @keydown.prevent.enter="toggleFilterWindow"
                @keydown.prevent.space="toggleFilterWindow"
              >
                <a
                  v-if="(!isFilterOpen || !isDesktop) && !isFilterApplied"
                  class="filter-headline"
                  >Nachrichten filtern</a
                >
                <a
                  v-if="(!isFilterOpen || !isDesktop) && isFilterApplied"
                  class="filter-headline"
                  >Filter anpassen</a
                >
                <span
                  v-if="!isFilterOpen || !isDesktop"
                  class="icon icon-filter open-filter-icon"
                ></span>
              </div>
              <div v-if="isFilterApplied" class="chip-container">
                <Chip
                  v-for="filter in activeFilters"
                  :key="filter.key"
                  :label="filter.value"
                  @close="() => onFilterDelete(filter.key)"
                />
              </div>
            </div>
            <div id="postbox-table">
              <PostboxTableContent
                :accountDocumentReadPermission="accountDocumentReadPermission"
                :documents-filtered="documentsFiltered"
              />
              <div
                v-if="documentsFiltered.length === 0"
                :class="{ 'ml-8': isDesktop }"
                class="mt-8 no-documents-found"
              >
                Keine Nachrichten gefunden
              </div>
            </div>
            <div v-if="isFilterApplied && isDesktop" class="desktop-reset-button">
              <CustomButton
                :button-color="ButtonColor.SECONDARY"
                :button-size="ButtonSize.SMALL"
                :disabled-state="!isFilterApplied"
                @click="resetFilter"
                >Filter zurücksetzen</CustomButton
              >
            </div>
          </div>
          <div v-else-if="!showErrorTile && noDocuments">
            <div v-if="loading" class="card-space">
              <Spinner />
            </div>
            <PostboxTableFrame v-if="!loading">
              <template #postboxTableBody>
                <tr :class="{ 'hide-row-separator': noDocuments }" class="postbox__row">
                  <td class="postbox__empty" colspan="4">
                    <span class="icon-oval-error"></span>
                    <span class="text">Sie haben noch keine Nachrichten erhalten!</span>
                  </td>
                </tr>
              </template>
            </PostboxTableFrame>
          </div>
        </template>
      </Card>
      <div v-else>
        <ErrorTile :title="$t('postbox.errorTitle')"></ErrorTile>
      </div>
    </div>
    <div
      v-if="isDesktop"
      v-show="isFilterOpen"
      class="wc-col wc-col__sm-6 wc-col__md-4 desktop-filter-container"
      tabindex="0"
    >
      <Card class="postbox-filter-card"
        ><template #content>
          <PostboxFilterHeader
            ref="filterHeader"
            :count="documentsFiltered.length"
            @close="closeFilter"
          />
          <PostboxFilterMain
            v-model:postboxPolicies="postboxPolicies"
            v-model:postboxStatus="postboxStatus"
            v-model:postboxZeitraum="postboxZeitraum"
            :documentsCount="documentsFiltered.length"
            :isDesktop="isDesktop"
            @toggleModal="toggleFilterWindow"
          />
          <PostboxFilterFooter
            :isFilterApplied="isFilterApplied"
            :postboxPolicies="postboxPolicies"
            :postboxStatus="postboxStatus"
            :postboxZeitraum="postboxZeitraum"
            @closeFilter="closeFilter"
            @resetFilter="resetFilter"
          />
        </template>
      </Card>
    </div>
  </div>

  <div>
    <div v-show="!isDesktop" id="postboxSwipeContainer" class="mobile-filter-container">
      <SwipeableBottomSheet
        ref="myBottomSheet"
        :max-height="700"
        @closed="closeFilterVariable"
        @opened="openFilterVariable"
      >
        <template #header>
          <div class="swipe-container">
            <PostboxFilterHeader :count="documentsFiltered.length" @close="closeFilter" />
          </div>
        </template>
        <template #default>
          <div>
            <PostboxFilterMain
              ref="PostboxFilterRef"
              v-model:postboxPolicies="postboxPolicies"
              v-model:postboxStatus="postboxStatus"
              v-model:postboxZeitraum="postboxZeitraum"
              :documentsCount="documentsFiltered.length"
              :isDesktop="isDesktop"
              @toggleModal="toggleFilterWindow"
            />
          </div>
        </template>
        <template #footer>
          <div class="swipe-container">
            <PostboxFilterFooter
              :isFilterApplied="isFilterApplied"
              :postboxPolicies="postboxPolicies"
              :postboxStatus="postboxStatus"
              :postboxZeitraum="postboxZeitraum"
              @closeFilter="closeFilter"
              @resetFilter="resetFilter"
            />
          </div>
        </template>
      </SwipeableBottomSheet>
    </div>
  </div>

  <div v-if="isFilterApplied && !isDesktop" class="mobile-reset-button">
    <CustomButton
      :button-color="ButtonColor.SECONDARY"
      :button-size="ButtonSize.SMALL"
      @click="resetFilter"
      >Filter zurücksetzen</CustomButton
    >
  </div>
</template>

<script lang="ts" setup>
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  ref
} from 'vue'
import {
  ButtonColor,
  ButtonSize,
  Card,
  Chip,
  CustomButton,
  Spinner
} from 'de-zurich-ui-library'
import PostboxTableFrame from '@/components/postbox/PostboxTableFrame.vue'
import { getToken, hasPermission, hasRole } from '@/utils/KeycloakUtils'
import { type Document } from '@/types/postbox/postbox-service'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import ErrorTile from '@/components/common/ErrorTile.vue'
import {
  type PostboxPolicy,
  PostboxStatus,
  postboxStatusLabels,
  PostboxZeitraum,
  postboxZeitraumShortLabels
} from '@/types/postbox/postbox-types'
import SwipeableBottomSheet from './SwipeableBottomSheet.sonarExclude.vue'
import PostboxFilterHeader from '@/components/postbox/PostboxFilterHeader.vue'
import PostboxFilterMain from '@/components/postbox/PostboxFilterMain.vue'
import PostboxFilterFooter from '@/components/postbox/PostboxFilterFooter.vue'
import PostboxTableContent from '@/components/postbox/PostboxTableContent.vue'
import { useWindowSize } from '@vueuse/core'

const { width } = useWindowSize()

dayjs.extend(customParseFormat)
dayjs.extend(isBetween)

dayjs.extend(customParseFormat)

const props = defineProps({
  partnerProxyId: {
    type: String,
    required: true
  },
  policyId: {
    type: String,
    required: false,
    default: null
  },
  isFilterActivated: {
    type: Boolean,
    default: true
  }
})

const loading = ref(true)
const showErrorTile = ref(false)
const noDocuments = ref(true)
const documents = ref<Array<Document>>([])
const isFilterOpen = ref(false)
const myBottomSheet = ref<InstanceType<typeof SwipeableBottomSheet>>()
const PostboxFilterRef = ref<InstanceType<typeof PostboxFilterMain>>()
const accountDocumentReadPermission = ref(false)
const postboxPolicies = ref<PostboxPolicy[]>([])
const postboxStatus = ref(PostboxStatus.ALL)
const postboxZeitraum = ref(PostboxZeitraum.ALL)
const isDesktop = ref(false)
const filterButton = ref<HTMLButtonElement | null>(null)
const filterHeader = ref<ComponentPublicInstance<{
  closeButton: HTMLElement | null
}> | null>(null)

const onResize = () => {
  isDesktop.value = width.value >= 600
}

onBeforeMount(() => {
  globalThis.addEventListener('resize', onResize)
  onResize()
})

const createQueryParams = (partnerProxyId: string, policyId: string | null) => {
  let params = { partnerProxyId }
  if (policyId) {
    params = Object.assign(params, { policyId })
  }
  return new URLSearchParams(params).toString()
}

const processDocuments = (documents: Document[]): PostboxPolicy[] => {
  const groupedDocuments = documents.reduce(
    (acc, document) => {
      if (!acc[document.kundenVSNr]) {
        acc[document.kundenVSNr] = []
      }
      acc[document.kundenVSNr]!.push(document)
      return acc
    },
    {} as Record<string, Document[]>
  )

  const seenKundenVSNr = new Set<string>()
  const filteredDocuments = Object.values(groupedDocuments).flatMap((group) => {
    if (group.length === 1) {
      return group
    }
    const nonSonstigesDocs = group.filter((doc) => doc.line !== 'SONSTIGES')
    const selectedDocs = nonSonstigesDocs.length > 0 ? nonSonstigesDocs : group
    return selectedDocs.filter((doc) => {
      if (seenKundenVSNr.has(doc.kundenVSNr)) {
        return false
      }
      seenKundenVSNr.add(doc.kundenVSNr)
      return true
    })
  })

  return filteredDocuments.map(
    (document) =>
      ({
        line: document.line,
        kundenVSNr: document.kundenVSNr,
        readStatus: document.readStatus,
        checked: true
      }) as PostboxPolicy
  )
}

onMounted(async () => {
  loading.value = true

  accountDocumentReadPermission.value =
    (hasPermission('documents:read') || hasRole('documents:read')) ?? false

  try {
    const token = await getToken()
    const queryParams = createQueryParams(props.partnerProxyId, props.policyId)

    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POSTBOX}/documents?${queryParams}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          KeyId: globalThis.ENV_CONFIG?.API_KEY,
          'Content-Type': 'application/json'
        }
      }
    )

    if (response.ok) {
      const responseData = await response.json()
      documents.value = responseData.documents
      postboxPolicies.value = processDocuments(documents.value)
      if (documents.value.length > 0) {
        noDocuments.value = false
      }
      showErrorTile.value = false
    } else {
      documents.value = []
      showErrorTile.value = true
      loading.value = false
    }
  } catch {
    documents.value = []
    showErrorTile.value = true
    loading.value = false
  } finally {
    loading.value = false
  }
})

const resetFilter = () => {
  PostboxFilterRef.value?.resetFilter()
}

const openFilter = () => {
  if (isDesktop.value) {
    openFilterVariable()
  } else {
    myBottomSheet.value?.open()
  }
}

const closeFilter = () => {
  if (isDesktop.value) {
    closeFilterVariable()
  } else {
    myBottomSheet.value?.close()
  }
}

const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

const openFilterVariable = () => {
  isFilterOpen.value = true
  debounce(() => {
    if (typeof globalThis !== 'undefined') {
      globalThis.dispatchEvent(new Event('resize'))
    }
  }, 100)()
  nextTick(() => {
    filterHeader.value?.closeButton?.focus()
  })
}

const closeFilterVariable = () => {
  isFilterOpen.value = false
  nextTick(() => {
    filterButton.value?.focus()
  })
}

const toggleFilterWindow = () => {
  if (isFilterOpen.value) {
    closeFilter()
  } else {
    openFilter()
  }
}

const allPoliciesSelected = computed(() => {
  return postboxPolicies.value.every((policy) => policy.checked)
})

const activeFilters = computed(() => {
  const filters = []
  if (!allPoliciesSelected.value)
    if (postboxPolicies.value.some((policy) => !policy.checked))
      filters.push({ key: 'vertrag', value: 'Verträge' })
  if (postboxZeitraum.value !== PostboxZeitraum.ALL) {
    filters.push({
      key: 'zeitraum',
      value:
        postboxZeitraumShortLabels[postboxZeitraum.value as PostboxZeitraum] ||
        postboxZeitraum.value
    })
  }
  if (postboxStatus.value !== PostboxStatus.ALL) {
    filters.push({
      key: 'status',
      value: postboxStatusLabels[postboxStatus.value as PostboxStatus]
    })
  }
  return filters
})

const onFilterDelete = (key: string): void => {
  if (key == 'vertrag') {
    postboxPolicies.value.forEach((policy) => {
      policy.checked = true
    })
  } else if (key == 'zeitraum') {
    postboxZeitraum.value = PostboxZeitraum.ALL
  } else if (key == 'status') {
    postboxStatus.value = PostboxStatus.ALL
  }
}

const computedClassesFilterOpen = computed(() => {
  let classes = 'wc-col'
  if (isFilterOpen.value) {
    classes = classes + ' filter-opened'
  }
  if (isDesktop.value && isFilterOpen.value) {
    classes = classes + ' wc-col wc-col__sm-6 wc-col__md-8'
  } else {
    classes = classes + ' wc-col wc-col__sm-12'
  }
  return classes
})

const filterDocumentsReadStatus = (documents: Document[], readStatus: PostboxStatus) => {
  return documents.filter((document) => {
    if (readStatus === PostboxStatus.READ) {
      return document.readStatus === PostboxStatus.READ
    } else if (readStatus === PostboxStatus.UNREAD) {
      return document.readStatus === PostboxStatus.UNREAD
    } else {
      return true
    }
  })
}

const filterDocumentsZeitraum = (documents: Document[], zeitraum: PostboxZeitraum) => {
  const now = dayjs()
  return documents.filter((document) => {
    const documentDate = dayjs(document.creationDate, 'DD.MM.YYYY')
    const timeDifference = now.diff(documentDate)
    const oneDay = 24 * 60 * 60 * 1000

    switch (zeitraum) {
      case PostboxZeitraum.LAST_30_D:
        return timeDifference <= 30 * oneDay
      case PostboxZeitraum.LAST_90_D:
        return timeDifference <= 90 * oneDay
      case PostboxZeitraum.LAST_6_M:
        return timeDifference <= 6 * 30 * oneDay
      case PostboxZeitraum.ALL:
      default: {
        if (zeitraum === PostboxZeitraum.ALL) {
          return true
        }
        const year = Number.parseInt(zeitraum)
        return documentDate.isBetween(
          dayjs(`${year}-01-01`),
          dayjs(`${year}-12-31`),
          null,
          '[]'
        )
      }
    }
  })
}

const documentsFiltered = computed(() => {
  let documentsTemp = documents.value
  documentsTemp = documentsTemp.filter((document) => {
    return postboxPolicies.value.find(
      (policy) => policy.checked && policy.kundenVSNr === document.kundenVSNr
    )
  })
  documentsTemp = filterDocumentsReadStatus(documentsTemp, postboxStatus.value)
  documentsTemp = filterDocumentsZeitraum(documentsTemp, postboxZeitraum.value)
  return documentsTemp
})

const isFilterApplied = computed(() => {
  return (
    postboxPolicies.value.some((policy) => !policy.checked) ||
    postboxStatus.value !== PostboxStatus.ALL ||
    postboxZeitraum.value !== PostboxZeitraum.ALL
  )
})
</script>

<style lang="scss" scoped>
:deep() .bottom-sheet__content,
:deep() .bottom-sheet__header {
  background: var(--postbox-filter-background);
}

.swipe-container {
  padding-left: 16px;
  padding-right: 16px;
}

/* Hide scrollbar for IE, Edge and Firefox */
.chip-container {
  gap: 11px;
  overflow-x: auto;
  max-width: 100%;
  display: flex;
  row-gap: 8px;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.chip-container::-webkit-scrollbar {
  display: none;
}

.mobile-reset-button {
  position: -webkit-sticky; /* For Safari */
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%; /* Full width of the viewport */
  padding: 16px;
  background-color: white;
  z-index: 1000; /* Ensure it stays on top */
  box-sizing: border-box; /* Include padding in the element's total width and height */
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1); /* Add shadow to the top border */
}

.desktop-reset-button {
  height: 56px;
  margin-top: 24px;
  .button {
    width: auto;
    float: right;
  }
}

.open-filter-icon {
  cursor: pointer;
  border-radius: 50%;
  padding: 3px;
  font-size: 20px;
  background-color: var(--link-color);
  color: white;
  float: right;
  margin-top: 4px;

  &:hover {
    background-color: var(--link-color-hover);
  }
}

:deep(.postbox-filter-card) {
  .card {
    border: var(--card-border-width) solid var(--postbox-filter-background);
  }
  .card__content {
    padding: 16px;
    border-radius: 17px;
    background-color: var(--postbox-filter-background);
  }
}

.filter-headline {
  font-size: var(--th-fontsize);
  font-family: var(--tr-highlighted-font-family) !important;
  color: var(--primary-color-0);
  margin-right: 16px;
  vertical-align: sub;
}

.filter-label {
  font-size: var(--small-font-size);
  color: var(--primary-color-0);
}

.status--deleted--postfach {
  margin-right: -4px !important;
  margin-top: 7px;
}

.postbox__row.disableHover {
  pointer-events: none;
}

@media (max-width: 599.98px) {
  .desktop-filter-container {
    display: none;
  }

  .postbox__date {
    width: calc(var(--grid-gutter-width) * 9) !important;
  }

  .status--deleted--postfach {
    margin-left: 8px;
  }
}

:deep() .postbox__row .postbox__action {
  text-align: center !important;
}

:deep() .postbox__row .postbox__action ~ .postbox__action {
  margin-bottom: 19px;
}

@media (hover) {
  .postbox__row:hover {
    color: var(--postbox-row-hover-color);
    cursor: pointer;

    .icon-download {
      color: var(--postbox-row-hover-color) !important;
    }
  }
}

.no-documents-found {
  font-size: var(--td-fontsize);
  font-family: var(--tr-highlighted-font-family);
}

.hide-row-separator {
  border-bottom: 0 !important;
}

.card-space {
  padding: 32px !important;
}

.data-table th {
  font-weight: 600;
}

.ml-8 {
  margin-left: 8px;
}

.postbox-header-container {
  margin-bottom: 8px;
}

.filter-row {
  margin-bottom: 8px;
}

@media (min-width: 600px) {
  .postbox-header-container {
    display: flex;
    flex-direction: row-reverse;
    gap: 32px;
    margin-right: 14px;
    margin-bottom: 4px;
  }

  .filter-row {
    margin-left: auto;
    text-align: right;
  }

  .mobile-filter-container {
    display: none;
  }

  .status--deleted--postfach {
    float: right;
  }
}

:deep(.button--cta2--disabled--small) {
  border: 2px solid var(--cta2-border-color-disabled) !important;
}

.wc-row {
  padding-bottom: 0;
}

.filter-row:focus-visible {
  outline: 2px solid var(--primary-color-0);
  outline-offset: 2px;
  border-radius: 3px;
}
</style>
